import { useState, useCallback, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useQueryClient, useIsFetching } from "@tanstack/react-query";
import { getSupabaseClient, useAuthStore } from "@veorest/api-client";
import {
  ShoppingBag,
  ClipboardList,
  Users,
  Bike,
  Monitor,
  Settings,
  Menu,
  X,
  LogOut,
  Bell,
  Clock,
  Sun,
  Moon,
  RefreshCw,
  Phone,
} from "lucide-react";
import PosPage from "./pages/PosPage";
import OrdersPage from "./pages/OrdersPage";
import DriversPage from "./pages/DriversPage";
import KdsPage from "./pages/KdsPage";
import LoginPage from "./pages/LoginPage";
import SettingsPage from "./pages/SettingsPage";
import LiveCallPage from "./pages/LiveCallPage";
import { useTenant } from "./hooks/useTenant";
import { I18nProvider, useI18n } from "./i18n";
import { OfflineBanner } from "@veorest/ui";
import { useBackendReachable, getQueueSize } from "@veorest/pwa";

type NavKey = "pos" | "orders" | "crm" | "drivers" | "kds" | "livecall" | "settings";

const NAV_ITEMS: { key: NavKey; label: string; icon: React.ReactNode; soon?: boolean }[] = [
  { key: "pos", label: "POS", icon: <ShoppingBag size={20} /> },
  { key: "orders", label: "Orders", icon: <ClipboardList size={20} /> },
  { key: "drivers", label: "Drivers", icon: <Bike size={20} /> },
  { key: "crm", label: "CRM", icon: <Users size={20} />, soon: true },
  { key: "kds", label: "KDS", icon: <Monitor size={20} /> },
  { key: "livecall", label: "Live Call", icon: <Phone size={20} /> },
  { key: "settings", label: "Settings", icon: <Settings size={20} /> },
];

function useClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return time.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
}

const ROUTE_TO_KEY: Record<string, NavKey> = {
  "/pos": "pos", "/orders": "orders", "/drivers": "drivers", "/kds": "kds", "/livecall": "livecall", "/settings": "settings",
};

function AppShell({ onSignOut, dark, toggleDark }: { onSignOut: () => void; dark: boolean; toggleDark: () => void }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useTenant();
  const { user } = useAuthStore();
  const { t, direction } = useI18n();
  const qc = useQueryClient();
  const isFetching = useIsFetching();
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const clock = useClock();

  const reachable = useBackendReachable();
  const [queueSize, setQueueSize] = useState(0);
  useEffect(() => {
    if (!reachable) {
      getQueueSize().then(setQueueSize);
      const interval = setInterval(() => { getQueueSize().then(setQueueSize); }, 5000);
      return () => clearInterval(interval);
    } else {
      setQueueSize(0);
    }
  }, [reachable]);

  const activeKey: NavKey = ROUTE_TO_KEY[location.pathname] ?? "pos";

  const handleNav = useCallback((key: NavKey) => {
    const paths: Record<string, string> = { pos: "/pos", orders: "/orders", drivers: "/drivers", kds: "/kds", livecall: "/livecall", settings: "/settings", crm: "#" };
    if (key === "crm") { window.open("https://app.oelox.com/app/customers", "_blank"); return; }
    const path = paths[key];
    if (path && path !== "#") { navigate(path); closeSidebar(); }
  }, [navigate, closeSidebar]);

  return (
    <div className="pos-app" dir={direction}>
      <div
        className={`pos-overlay ${sidebarOpen ? "pos-overlay--visible" : ""}`}
        onClick={closeSidebar}
      />

      <aside className={`pos-sidebar ${sidebarOpen ? "pos-sidebar--open" : ""}`}>
        <div className="pos-sidebar__header">
          <div className="pos-sidebar__logo">O</div>
          <span className="pos-sidebar__brand">OELOX</span>
        </div>

        <nav className="pos-sidebar__nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              className={`pos-sidebar__item ${activeKey === item.key ? "pos-sidebar__item--active" : ""}`}
              onClick={() => handleNav(item.key)}
            >
              {item.icon}
              <span>{t("nav." + item.key) || item.label}</span>
              {item.soon && <span className="pos-sidebar__badge">{t("nav.soon")}</span>}
            </button>
          ))}
        </nav>

        <div style={{ padding: "0 10px 10px" }}>
          <button className="pos-sidebar__item" onClick={onSignOut}>
            <LogOut size={20} />
            <span>{t("nav.signOut") || "Sign Out"}</span>
          </button>
        </div>

        <div className="pos-sidebar__footer">OELOX v1.0</div>
      </aside>

      <main className="pos-main">
        {/* ── Global offline banner ── */}
        {!reachable && (
          <OfflineBanner className="!static !top-auto !z-40" />
        )}
        {!reachable && queueSize > 0 && (
          <div style={{
            background: "var(--warning)", color: "#fff", fontSize: 11, fontWeight: 700,
            textAlign: "center", padding: "3px 8px",
          }}>
            {queueSize} action{queueSize !== 1 ? "s" : ""} pending sync
          </div>
        )}
        {/* ── Unified Pink Header (burger + branding + clock + actions) ── */}
        <div className="pos-header">
          <button className="pos-header__burger" onClick={() => setSidebarOpen((v) => !v)} aria-label={t("header.toggleSidebar")}>
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <span className="pos-header__restaurant">{tenant?.name ?? "Restaurant"}</span>
          <span className="pos-header__staff">{user?.email?.split("@")[0] ?? "Staff"}</span>
          <div className="pos-header__spacer" />
          <span className="pos-header__clock"><Clock size={14} style={{ marginInlineEnd: 5 }} />{clock}</span>
          <button className="pos-header__icon" aria-label={t("header.notifications")}><Bell size={18} /></button>
          <button className="pos-header__icon" onClick={() => qc.invalidateQueries()} aria-label="Refresh" title="Refresh"><RefreshCw size={18} className={isFetching > 0 ? "animate-spin" : ""} /></button>
          <button className="pos-header__icon" onClick={toggleDark} aria-label={t("header.toggleTheme")}>
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button className="pos-header__icon" onClick={() => navigate("/settings")} aria-label={t("header.settings")}><Settings size={18} /></button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <Routes>
            <Route path="/" element={<Navigate to="/pos" replace />} />
            <Route path="/pos" element={<PosPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/drivers" element={<DriversPage />} />
            <Route path="/kds" element={<KdsPage />} />
            <Route path="/livecall" element={<LiveCallPage />} />
            <Route path="/settings" element={<SettingsPage dark={dark} toggleDark={toggleDark} onSignOut={onSignOut} />} />
            <Route path="*" element={<Navigate to="/pos" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default function App() {
  const { user, setUser, setSession, setLoading } = useAuthStore();
  const [ready, setReady] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("oelox-pos-theme") === "dark");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
    localStorage.setItem("oelox-pos-theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const supabase = getSupabaseClient();

    // Primary: Supabase getSession (reads from localStorage, works offline)
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      setReady(true);
    }).catch(() => {
      // getSession failed (network error, corrupted storage) — fall through to timeout
    });

    // Safety: if getSession hangs or fails, use Zustand-persisted session after 3s
    const safetyTimer = setTimeout(() => {
      setReady((current) => {
        if (current) return current; // already resolved
        const stored = useAuthStore.getState();
        if (stored.user) {
          setLoading(false);
        }
        return true; // force ready even without session (shows login page)
      });
    }, 3000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      setReady(true);
    });
    return () => {
      subscription.unsubscribe();
      clearTimeout(safetyTimer);
    };
  }, [setUser, setSession, setLoading]);

  const handleSignOut = async () => {
    await getSupabaseClient().auth.signOut();
    setUser(null);
    setSession(null);
  };

  if (!ready) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100dvh", background: "#0a0a0b", color: "#94a3b8", fontSize: 14 }}>
        Loading…
      </div>
    );
  }

  return (
    <I18nProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/pos" replace /> : <LoginPage />} />
          <Route
            path="/*"
            element={
              user ? <AppShell onSignOut={handleSignOut} dark={dark} toggleDark={() => setDark((d) => !d)} /> : <Navigate to="/login" replace />
            }
          />
        </Routes>
      </BrowserRouter>
    </I18nProvider>
  );
}
